import React, { useState } from 'react';
import { FaRupeeSign, FaChartLine, FaCalendar, FaPercentage, FaInfoCircle, FaQuestionCircle } from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const ELSSCalculator = () => {
  const [investmentAmount, setInvestmentAmount] = useState('');
  const [investmentPeriod, setInvestmentPeriod] = useState('');
  const [expectedReturn, setExpectedReturn] = useState('');
  
  const [totalInvestment, setTotalInvestment] = useState('');
  const [maturityValue, setMaturityValue] = useState('');
  const [taxSaved, setTaxSaved] = useState('');
  const [totalReturns, setTotalReturns] = useState('');
  const [monthlyBreakdown, setMonthlyBreakdown] = useState([]);

  const calculateELSS = () => {
    if (!investmentAmount || !investmentPeriod || !expectedReturn) return;

    const P = parseFloat(investmentAmount);
    const years = parseFloat(investmentPeriod);
    const r = parseFloat(expectedReturn) / 100;

    // Calculate total investment
    const totalInv = P * years;
    setTotalInvestment(totalInv.toFixed(2));

    // Calculate maturity value using compound interest formula
    const maturityVal = P * Math.pow(1 + r, years);
    setMaturityValue(maturityVal.toFixed(2));

    // Calculate tax saved (25% of investment amount)
    const taxSavedAmount = P * 0.25;
    setTaxSaved(taxSavedAmount.toFixed(2));

    // Calculate total returns
    const totalReturnsAmount = maturityVal - totalInv;
    setTotalReturns(totalReturnsAmount.toFixed(2));

    // Calculate monthly breakdown
    const breakdown = [];
    let currentAmount = P;
    for (let i = 1; i <= years; i++) {
      currentAmount = currentAmount * (1 + r);
      breakdown.push({
        year: i,
        value: currentAmount
      });
    }
    setMonthlyBreakdown(breakdown);
  };

  const chartData = {
    labels: monthlyBreakdown.map(item => `Year ${item.year}`),
    datasets: [
      {
        label: 'Investment Growth',
        data: monthlyBreakdown.map(item => item.value),
        borderColor: '#4ecdc4',
        tension: 0.1,
        fill: true,
        backgroundColor: 'rgba(78, 205, 196, 0.1)'
      }
    ],
  };

  return (
    <div className="bg-gradient-to-br from-blue-100 to-purple-100 min-h-screen p-6">
      <Helmet>
        <title>ELSS Calculator</title>
        <meta name="description" content="Calculate your ELSS investment returns, tax savings, and growth with our easy-to-use calculator." />
        <meta property="og:title" content="ELSS Calculator" />
        <meta property="og:description" content="Calculate your ELSS investment returns, tax savings, and growth with our easy-to-use calculator." />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-gray-800">
            <FaChartLine className="inline-block mr-2 text-green-500" />
            ELSS Calculator
          </h2>
          <div className="flex items-center space-x-4">
            <button className="text-green-500 hover:text-green-600">
              <FaInfoCircle className="w-6 h-6" />
            </button>
            <button className="text-green-500 hover:text-green-600">
              <FaQuestionCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-green-50 p-4 rounded-xl">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Annual Investment (₹)
            </label>
            <div className="relative">
              <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                value={investmentAmount}
                onChange={(e) => setInvestmentAmount(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                placeholder="Enter annual investment amount"
              />
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-xl">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Investment Period (Years)
            </label>
            <div className="relative">
              <FaCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                value={investmentPeriod}
                onChange={(e) => setInvestmentPeriod(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                placeholder="Enter investment period"
              />
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-xl">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Expected Return (% p.a.)
            </label>
            <div className="relative">
              <FaPercentage className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                value={expectedReturn}
                onChange={(e) => setExpectedReturn(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                placeholder="Enter expected return rate"
              />
            </div>
          </div>
        </div>

        <button
          onClick={calculateELSS}
          className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-black font-bold py-3 px-6 rounded-xl hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-green-200 focus:ring-offset-2 transition-all duration-200"
        >
          Calculate Returns
        </button>

        {maturityValue && (
          <div className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-green-100 p-6 rounded-xl text-center">
                <h3 className="text-2xl font-bold text-green-600">
                  <FaRupeeSign className="inline-block mr-1" />
                  {totalInvestment}
                </h3>
                <p className="text-gray-600">Total Investment</p>
              </div>

              <div className="bg-green-100 p-6 rounded-xl text-center">
                <h3 className="text-2xl font-bold text-green-600">
                  <FaRupeeSign className="inline-block mr-1" />
                  {maturityValue}
                </h3>
                <p className="text-gray-600">Maturity Value</p>
              </div>

              <div className="bg-green-100 p-6 rounded-xl text-center">
                <h3 className="text-2xl font-bold text-green-600">
                  <FaRupeeSign className="inline-block mr-1" />
                  {taxSaved}
                </h3>
                <p className="text-gray-600">Tax Saved</p>
              </div>

              <div className="bg-green-100 p-6 rounded-xl text-center">
                <h3 className="text-2xl font-bold text-green-600">
                  <FaRupeeSign className="inline-block mr-1" />
                  {totalReturns}
                </h3>
                <p className="text-gray-600">Total Returns</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-xl font-bold mb-4">Investment Growth</h3>
              <div className="w-full h-[500px]">
                <Line data={chartData} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'top',
                      labels: {
                        font: {
                          size: 16
                        },
                        padding: 20
                      }
                    },
                    title: {
                      display: true,
                      text: 'Investment Growth Over Time',
                      font: {
                        size: 18
                      },
                      padding: 20
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          const label = context.dataset.label;
                          const value = context.parsed.y;
                          return `${label}: ₹${value.toLocaleString()}`;
                        }
                      },
                      padding: 10,
                      titleFont: {
                        size: 14
                      },
                      bodyFont: {
                        size: 14
                      }
                    }
                  },
                  scales: {
                    x: {
                      title: {
                        display: true,
                        text: 'Year',
                        font: {
                          size: 16
                        },
                        padding: 20
                      },
                      ticks: {
                        font: {
                          size: 14
                        },
                        autoSkip: true,
                        maxTicksLimit: 20,
                        padding: 10
                      },
                      grid: {
                        display: false
                      }
                    },
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Amount (₹)',
                        font: {
                          size: 16
                        },
                        padding: 20
                      },
                      ticks: {
                        callback: function(value) {
                          return '₹' + value.toLocaleString();
                        },
                        font: {
                          size: 14
                        },
                        padding: 10
                      },
                      grid: {
                        drawBorder: false,
                        tickColor: 'rgba(0, 0, 0, 0.1)'
                      }
                    }
                  },
                  animation: {
                    duration: 1500,
                    easing: 'easeInOutQuad',
                    delay: 200
                  }
                }} />
              </div>
            </div>
          </div>
        )}

        <div className="mt-12">
          <h3 className="text-2xl font-bold mb-6">Benefits of ELSS</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-green-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">Tax Benefits</h4>
              <p className="text-gray-600">Investments in ELSS qualify for tax deduction under Section 80C, up to ₹1.5 lakh per year.</p>
            </div>
            <div className="bg-green-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">Higher Returns</h4>
              <p className="text-gray-600">ELSS typically offers higher returns compared to other tax-saving instruments due to its equity exposure.</p>
            </div>
            <div className="bg-green-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">Lock-in Period</h4>
              <p className="text-gray-600">ELSS has a lock-in period of just 3 years, which is shorter than other tax-saving instruments.</p>
            </div>
            <div className="bg-green-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">Diversification</h4>
              <p className="text-gray-600">ELSS provides exposure to a diversified portfolio of stocks, reducing risk while maximizing returns.</p>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <h3 className="text-2xl font-bold mb-6">How to Use</h3>
          <ol className="list-decimal list-inside space-y-4">
            <li>
              <div className="bg-green-50 p-4 rounded-xl">
                <h4 className="text-xl font-semibold mb-2">Enter Investment Details</h4>
                <p className="text-gray-600">Input your annual investment amount, expected investment period, and expected return rate.</p>
              </div>
            </li>
            <li>
              <div className="bg-green-50 p-4 rounded-xl">
                <h4 className="text-xl font-semibold mb-2">Calculate Returns</h4>
                <p className="text-gray-600">Click the "Calculate Returns" button to see your potential returns, tax savings, and investment growth.</p>
              </div>
            </li>
            <li>
              <div className="bg-green-50 p-4 rounded-xl">
                <h4 className="text-xl font-semibold mb-2">View Growth Chart</h4>
                <p className="text-gray-600">Check the investment growth chart to visualize how your investment will grow over time.</p>
              </div>
            </li>
          </ol>
        </div>

        <div className="mt-12">
          <h3 className="text-2xl font-bold mb-6">FAQ</h3>
          <div className="space-y-6">
            <div className="bg-green-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">What is ELSS?</h4>
              <p className="text-gray-600">ELSS stands for Equity Linked Savings Scheme. It's a tax-saving mutual fund that invests primarily in equities and qualifies for tax benefits under Section 80C.</p>
            </div>
            <div className="bg-green-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">What is the lock-in period for ELSS?</h4>
              <p className="text-gray-600">ELSS has a lock-in period of 3 years, which is the shortest among all tax-saving instruments under Section 80C.</p>
            </div>
            <div className="bg-green-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">How much tax can I save with ELSS?</h4>
              <p className="text-gray-600">You can save tax on up to ₹1.5 lakh of investment in ELSS per year under Section 80C. The actual tax saved depends on your tax slab.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ELSSCalculator;
